(() => {
  const MIN_LOADER_MS = 300;
  const TOP_IMAGE_TIMEOUT_MS = 1200;

  const clearLayoutLoading = () => document.documentElement.classList.remove('layout-loading');

  const init = () => {
    if (window.Shopify?.designMode) { clearLayoutLoading(); return; }
    if (window.simply?.EOSSEnable) { clearLayoutLoading(); return; }

    const configEl = document.getElementById('layout-engine-config');
    if (!configEl) { clearLayoutLoading(); return; }

    let config;
    try {
      config = JSON.parse(configEl.textContent);
    } catch (error) {
      console.warn('[layout-engine] invalid config', error);
      clearLayoutLoading();
      return;
    }


    const layouts = (config?.layouts ?? []).map((layout, position) => ({ ...layout, index: position + 1 }));
    if (!layouts.length) { clearLayoutLoading(); return; }

    const mainEl = document.getElementById('main');
    if (!mainEl) { clearLayoutLoading(); return; }

    const root = window.Shopify?.routes?.root ?? '/';

    const loader = document.querySelector('.layout-engine-loader');
    if (loader) document.body.appendChild(loader);

    let loaderShownAt = 0;
    const showLoader = () => {
      loaderShownAt = performance.now();
      loader?.classList.remove('is-hidden');
    };
    const hideLoader = async () => {
      const elapsed = performance.now() - loaderShownAt;
      if (elapsed < MIN_LOADER_MS) {
        await new Promise((resolve) => setTimeout(resolve, MIN_LOADER_MS - elapsed));
      }
      loader?.classList.add('is-hidden');
      clearLayoutLoading();
    };

    const layoutCache = new Map();
    const warmImages = new Map();

    const findById = (id) => layouts.find((layout) => layout.id === id) ?? null;
    const findByPageView = (pageView) => layouts.find((layout) => layout.pageView === pageView) ?? null;

    const setMain = (html) => {
      document.getElementById('main').innerHTML = html;
    };

    const topImageDescriptor = (container) => {
      const img = container.querySelector('img');
      if (!img) return null;
      let srcset = img.getAttribute('srcset') ?? '';
      const sizes = img.getAttribute('sizes') ?? '100vw';
      const src = img.getAttribute('src') ?? '';
      const picture = img.closest('picture');
      if (picture) {
        for (const source of picture.querySelectorAll('source')) {
          const media = source.getAttribute('media');
          if (media && window.matchMedia(media).matches) {
            srcset = source.getAttribute('srcset') ?? srcset;
            break;
          }
        }
      }
      return { srcset, sizes, src };
    };

    const warmImage = (descriptor) => {
      if (!descriptor) return null;
      const img = new Image();
      img.sizes = descriptor.sizes;
      if (descriptor.srcset) img.srcset = descriptor.srcset;
      if (descriptor.src) img.src = descriptor.src;
      return img;
    };

    const decodeImage = async (img) => {
      if (!img) return;
      try {
        const decoding = typeof img.decode === 'function' ? img.decode() : Promise.resolve();
        await Promise.race([
          decoding,
          new Promise((resolve) => setTimeout(resolve, TOP_IMAGE_TIMEOUT_MS)),
        ]);
      } catch (error) {
        console.warn('[layout-engine] top image decode failed, revealing anyway', error);
      }
    };

    const homeEntry = { html: mainEl.innerHTML, descriptor: topImageDescriptor(mainEl) };


    const extractMainInner = (html) => {
      const openTag = html.match(/<main\b[^>]*\bid=["']main["'][^>]*>/i);
      if (!openTag) return null;
      const start = openTag.index + openTag[0].length;
      const end = html.indexOf('</main>', start);
      if (end === -1) return null;
      return html.slice(start, end);
    };

    const fetchLayoutMain = async (layout) => {
      if (!layout.fetchUrl) return null;
      const response = await fetch(layout.fetchUrl);
      if (!response.ok) return null;
      const html = await response.text();
      const mainInner = extractMainInner(html);
      if (mainInner != null) return new DOMParser().parseFromString(mainInner, 'text/html').body;
      return new DOMParser().parseFromString(html, 'text/html').getElementById('main');
    };

    const prefetch = async (layout) => {
      if (layoutCache.has(layout.id)) return;
      try {
        const main = await fetchLayoutMain(layout);
        if (!main) return;
        const descriptor = topImageDescriptor(main);
        layoutCache.set(layout.id, { html: main.innerHTML, descriptor });
        const warm = warmImage(descriptor);
        if (warm) warmImages.set(layout.id, warm);
      } catch (error) {
        console.warn('[layout-engine] prefetch failed', layout.fetchUrl, error);
      }
    };

    const SLOW_CONNECTIONS = ['slow-2g', '2g', '3g'];
    const requestIdle = (callback) =>
      (window.requestIdleCallback ?? ((cb) => setTimeout(cb, 200)))(callback, { timeout: 2000 });

    const startPrefetch = () => {
      const connection = navigator.connection;
      if (connection?.saveData) return;
      if (connection && SLOW_CONNECTIONS.includes(connection.effectiveType)) return;

      const queue = layouts.filter((layout) => layout.fetchUrl);
      const runNext = async () => {
        const layout = queue.shift();
        if (!layout) return;
        await prefetch(layout);
        if (queue.length) requestIdle(runNext);
      };
      requestIdle(runNext);
    };

    const reinit = (container) => {
      const printLinks = [...container.querySelectorAll('link[rel="stylesheet"][media="print"]')];
      for (const link of printLinks) link.media = 'all';


      const scripts = [...container.querySelectorAll('script[src]')];
      for (const oldScript of scripts) {
        const src = oldScript.getAttribute('src');
        oldScript.remove();
        if (!src) continue;
        const alreadyAdded = document.querySelector(`script[data-layout-engine-src="${src}"]`);
        const alreadyLoaded = [...document.scripts].some((script) => script.src === oldScript.src);
        if (alreadyAdded || alreadyLoaded) continue;
        const newScript = document.createElement('script');
        newScript.src = src;
        newScript.setAttribute('data-layout-engine-src', src);
        document.body.appendChild(newScript);
      }

      const sections = [...container.querySelectorAll('.shopify-section')];
      for (const section of sections) {
        section.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
      }
    };

    // The engine owns ONLY the homepage #main content + the ?page_view URL. State, the active tab in
    // BOTH menus, the drawer panel/swiper, and persistence belong to NBLayout (nb-layout.js).
    // applySwap therefore never touches chrome or persistence — it just renders content + URL.
    let busy = false;
    let pendingSwitch = null;
    let requestedKey = null;
    const keyOf = (layout) => (layout ? layout.id : '');

    const applySwap = (innerHtml, layout, doPush) => {
      setMain(innerHtml);
      reinit(document.getElementById('main'));
      window.scrollTo(0, 0);
      if (doPush) {
        if (layout) {
          history.pushState({ pageView: layout.pageView }, '', `${root}?page_view=${layout.pageView}`);
        } else {
          history.pushState({ pageView: '' }, '', root);
        }
      }
      clearLayoutLoading();
    };

    const drainPendingSwitch = () => {
      if (!pendingSwitch) return;
      const next = pendingSwitch;
      pendingSwitch = null;
      switchTo(next.layout, next.doPush);
    };

    const switchTo = async (layout, doPush) => {
      requestedKey = keyOf(layout);

      if (busy) { pendingSwitch = { layout, doPush }; return; }

      const entry = !layout ? homeEntry : (layoutCache.get(layout.id) ?? null);

      if (entry != null) {
        busy = true;
        try {

          const warm = (layout && warmImages.get(layout.id)) || warmImage(entry.descriptor);
          await decodeImage(warm);
          if (keyOf(layout) === requestedKey) applySwap(entry.html, layout, doPush);
        } finally {
          busy = false;
          drainPendingSwitch();
        }
        return;
      }

      busy = true;
      showLoader();
      try {
        const main = await fetchLayoutMain(layout);
        if (!main) {
          console.error('[layout-engine] fetch failed', layout.fetchUrl);
          return;
        }
        const fetchedEntry = { html: main.innerHTML, descriptor: topImageDescriptor(main) };
        layoutCache.set(layout.id, fetchedEntry);
        await decodeImage(warmImage(fetchedEntry.descriptor));
        if (keyOf(layout) === requestedKey) applySwap(fetchedEntry.html, layout, doPush);
      } finally {
        await hideLoader();
        busy = false;
        drainPendingSwitch();
      }
    };

    // Content entry point, called by NBLayout.set() on the homepage. Pure: swap #main + push the URL.
    // Idempotent: if the URL already shows the target, do nothing (avoids a duplicate pushState on a
    // fast multi-slide flick that debounces down to the slide it's already on).
    window.layoutEngineGoTo = (id, push) => {
      const layout = id ? findById(id) : null;
      if (id && !layout) return;                                        // unknown category (e.g. SALE)
      const current = new URLSearchParams(window.location.search).get('page_view') || '';
      const target = layout ? layout.pageView : '';                     // home has no page_view
      if (current === target) return;                                   // already here — idempotent no-op
      switchTo(layout, push !== false);
    };

    // Is `id` a real engine layout? Answered from the in-memory config (kept in this closure), NOT by
    // reading #layout-engine-config from the DOM — that element lives inside #main and is DESTROYED on
    // the first content swap, so any DOM-based lookup at click time would intermittently return nothing.
    window.layoutEngineHas = (id) => {
      const key = (id == null ? '' : String(id)).trim().toUpperCase();
      if (!key) return false;
      return layouts.some((layout) => (layout.id || '').trim().toUpperCase() === key);
    };

    window.layoutEngineActiveId = () => {
      const pageView = new URLSearchParams(window.location.search).get('page_view');
      if (!pageView || pageView === 'home') return '';
      const layout = findByPageView(pageView);
      return layout ? layout.id : '';
    };

    // Back/Forward: the URL has ALREADY changed, so we sync the chrome via NBLayout (content:false —
    // don't route back through layoutEngineGoTo, whose idempotence guard would see the URL already
    // matches and skip the swap) and swap #main directly with no push.
    window.addEventListener('popstate', () => {
      const pageView = new URLSearchParams(window.location.search).get('page_view');
      const layout = pageView && pageView !== 'home' ? findByPageView(pageView) : null;
      if (window.NBLayout) window.NBLayout.set(layout ? layout.id : '', { content: false, push: false });
      switchTo(layout, false);
    });

    // Use the single persistence reader (nb_layout cookie) so the homepage restore matches what the
    // drawer shows on inner pages. Falls back to ACTIVE_TAB only if NBLayout is absent.
    const readPersistedLayoutId = () =>
      (window.NBLayout ? window.NBLayout.readPersisted() : (String(window.simply?.cartObj?.attributes?.ACTIVE_TAB || '').split('_')[0] || '').trim().toUpperCase());

    // Initial content render. Chrome for all three cases is already painted by nb-layout.js
    // (resolveInitial uses the SAME resolution), so the engine only swaps #main + normalises the URL.
    const initialPageView = new URLSearchParams(window.location.search).get('page_view');
    if (initialPageView && initialPageView !== 'home') {
      // Deep link to a specific layout: show it. URL is already correct, so don't push.
      const layout = findByPageView(initialPageView);
      if (layout) switchTo(layout, false);
      else clearLayoutLoading();
    } else if (initialPageView === 'home') {
      // Stray "?page_view=home" (home has no param): normalise the URL to "/" and keep the SSR home.
      history.replaceState({ pageView: '' }, '', root);
      clearLayoutLoading();
    } else {
      // Plain "/" (new session / external link): restore the last layout if one is persisted, rewriting
      // the URL in place (no extra history entry). If nothing is persisted, home stays a bare "/".
      const restoreId = readPersistedLayoutId();
      const layout = restoreId ? findById(restoreId) : null;
      if (layout) {
        history.replaceState({ pageView: layout.pageView }, '', `${root}?page_view=${layout.pageView}`);
        switchTo(layout, false);
      } else {
        clearLayoutLoading();
      }
    }

    if (document.readyState === 'complete') {
      startPrefetch();
    } else {
      window.addEventListener('load', startPrefetch, { once: true });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
